import React, { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import * as endPoints from "../../constants/v2/endPoints";

const SUGGESTIONS = [
  "How does matching work?",
  "How do I post a Want-Listing?",
  "What is an Introduction?",
  "How do I add inventory?",
];

const HelpChatPanel = ({ context, onEscalateToTicket }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi - I'm your BLS help assistant. Ask me anything about the platform - how matching works, how to post a Want-Listing, how to send introductions, etc. If I can't help, I'll create a ticket for the support team.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || streaming) return;

    const nextMessages = [
      ...messages,
      { role: "user", content: trimmed },
      { role: "assistant", content: "" },
    ];
    setMessages(nextMessages);
    setInput("");
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const apiPayload = {
        messages: nextMessages
          .slice(0, -1)
          .map(({ role, content }) => ({ role, content })),
        context,
      };
      const response = await fetch(endPoints.HELP_CHAT_STREAM, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-key": process.env.REACT_APP_REST_API_KEY,
        },
        body: JSON.stringify(apiPayload),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`Chat request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";

      const flushBuffer = () => {
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n");
          let eventName = "message";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let parsed;
          try {
            parsed = JSON.parse(dataStr);
          } catch (_e) {
            continue;
          }
          if (eventName === "chunk" && typeof parsed.text === "string") {
            assistantText += parsed.text;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                role: "assistant",
                content: assistantText,
              };
              return copy;
            });
          } else if (eventName === "error") {
            throw new Error(parsed.message || "Chat error");
          }
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        flushBuffer();
      }
      buffer += decoder.decode();
      flushBuffer();
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Something went wrong on my side. You can send a ticket to support and the team will follow up.",
          };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleEscalate = () => {
    const transcript = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-10)
      .map(
        (m) =>
          `${m.role === "user" ? "Me" : "Assistant"}: ${m.content || "(empty)"}`
      )
      .join("\n\n");
    onEscalateToTicket && onEscalateToTicket(transcript);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="help-chat-panel">
      <div className="chat-thread" ref={scrollRef}>
        {messages.map((m, idx) => (
          <div key={idx} className={`chat-bubble role-${m.role}`}>
            {m.role === "assistant" ? (
              <div className="bubble-content">
                {m.content || <span className="typing-dots">...</span>}
              </div>
            ) : (
              <div className="bubble-content">{m.content}</div>
            )}
          </div>
        ))}
        {streaming && (
          <div className="chat-bubble role-assistant streaming-cursor">
            <div className="bubble-content">
              <span className="typing-dots">...</span>
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="chat-suggestions">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="suggestion-chip"
              onClick={() => send(s)}
              disabled={streaming}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="chat-input-row">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask anything about the platform..."
          rows={2}
          disabled={streaming}
        />
        <Button
          variant="primary"
          onClick={() => send()}
          disabled={!input.trim() || streaming}
        >
          {streaming ? "..." : "Send"}
        </Button>
      </div>

      <div className="chat-escalate-row">
        <span>Need a human?</span>
        <button
          type="button"
          className="escalate-link"
          onClick={handleEscalate}
        >
          Send a ticket to support
        </button>
      </div>
    </div>
  );
};

export default HelpChatPanel;
